import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { formatApiError } from "../lib/api";
import { MODULES } from "../data/modules";

const field = "w-full bg-white border border-linen rounded-2xl px-5 py-4 text-sm text-ink placeholder:text-smoke/60 focus:outline-none focus:ring-2 focus:ring-sage/50 transition-shadow duration-300";

export default function Auth() {
  const [params] = useSearchParams();
  const { t } = useI18n();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const moduleParam = params.get("module");
  const preselected = moduleParam != null ? MODULES[Number(moduleParam)] : null;

  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") {
        await register(form.name, form.email, form.password);
        if (preselected) {
          const { default: api, startModuleCheckout } = await import("../lib/api");
          if (preselected.price > 0) {
            toast.success(t("auth.submitRegister"));
            await startModuleCheckout(preselected.id);
            return;
          }
          await api.post("/subscriptions", { module_id: preselected.id });
          toast.success(`${t("sim.activate")} ${preselected.name}`);
        } else {
          toast.success(t("auth.submitRegister"));
        }
      } else {
        await login(form.email, form.password);
        toast.success(t("auth.welcomeBack"));
      }
      navigate("/espace-client");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="min-h-screen bg-paper flex">
      <div className="hidden lg:flex w-[42%] bg-ink text-paper flex-col justify-between p-14">
        <Link to="/" aria-label="Accueil VAV"><Logo light /></Link>
        <div>
          <p className="font-serif italic text-3xl md:text-4xl leading-snug max-w-md">
            « Sans Vision, l'Autorité ne peut entraîner les hommes vers la Vertu agissante. »
          </p>
          <p className="mt-8 text-xs uppercase tracking-[0.28em] text-paper/50">Cabinet VAV — Assurance vie professionnelle</p>
        </div>
        <p className="text-xs text-paper/40">France Travail — sponsor officiel</p>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-block mb-10" aria-label="Accueil VAV"><Logo /></Link>

          {preselected && (
            <div data-testid="auth-preselected-module" className="mb-8 rounded-2xl bg-bone px-6 py-4 text-sm text-smoke">
              {t("auth.preselected")} : <span className="font-serif text-lg text-ink">{preselected.name}</span>
              <span className="ml-2 text-sagedark">{preselected.priceLabel}{preselected.price > 0 ? ` ${t("sim.perMonth")}` : ""}</span>
            </div>
          )}

          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">
            {mode === "register" ? t("auth.createSpace") : t("auth.welcomeBack")}
          </h1>
          <p className="mt-3 text-sm text-smoke">
            {mode === "register" ? t("auth.subRegister") : t("auth.subLogin")}
          </p>

          <div className="mt-8 flex rounded-full bg-bone p-1" data-testid="auth-mode-tabs">
            {[["login", t("auth.tabLogin")], ["register", t("auth.tabRegister")]].map(([m, label]) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 rounded-full py-2.5 text-sm transition-colors duration-300 ${mode === m ? "bg-ink text-paper" : "text-smoke hover:text-ink"}`}
              >
                {label}
              </button>
            ))}
          </div>

          <form data-testid="auth-form" onSubmit={submit} className="mt-8 flex flex-col gap-5">
            {mode === "register" && (
              <div>
                <label htmlFor="auth-name" className="text-xs uppercase tracking-[0.2em] text-smoke">{t("auth.name")}</label>
                <input id="auth-name" data-testid="auth-name-input" className={`${field} mt-2`} placeholder="Camille Laurent" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
            )}
            <div>
              <label htmlFor="auth-email" className="text-xs uppercase tracking-[0.2em] text-smoke">{t("auth.email")}</label>
              <input id="auth-email" data-testid="auth-email-input" type="email" className={`${field} mt-2`} placeholder="vous@exemple.fr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div>
              <label htmlFor="auth-password" className="text-xs uppercase tracking-[0.2em] text-smoke">{t("auth.password")}</label>
              <input id="auth-password" data-testid="auth-password-input" type="password" className={`${field} mt-2`} placeholder={mode === "register" ? t("auth.pwdNew") : t("auth.pwdPh")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={mode === "register" ? 8 : 1} />
            </div>

            {error && <p data-testid="auth-error" className="text-sm text-red-700 bg-red-50 rounded-xl px-4 py-3">{error}</p>}

            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="mt-2 rounded-full bg-ink text-paper px-8 py-4 text-sm tracking-wide hover:bg-sagedark disabled:opacity-50 transition-colors duration-300"
            >
              {loading ? t("auth.wait") : mode === "register" ? t("auth.submitRegister") : t("auth.submitLogin")}
            </button>
          </form>

          <p className="mt-8 text-sm text-smoke">
            <Link data-testid="auth-back-home" to="/" className="underline underline-offset-4 hover:text-ink transition-colors duration-300">{t("auth.backHome")}</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
